import { useState } from 'react'
import { X, Printer, Loader2, CheckSquare, Square } from 'lucide-react'
import clientesService from '../../services/clientesService'

/**
 * Modal de impressão do cliente — permite escolher quais seções (abas)
 * entram no PDF: só a completa, ou uma seleção (ex.: só Dados Bancários).
 */
export default function ImprimirClienteModal({ cliente, organizadorId, labelFazendas = 'Fazendas', temCadastroPublico = false, onClose }) {
  const SECOES_DISPONIVEIS = [
    { id: 'dados', label: 'Dados Principais' },
    { id: 'endereco', label: 'Endereço' },
    { id: 'fazendas', label: labelFazendas },
    { id: 'bancarios', label: 'Dados Bancários' },
    { id: 'comissoes', label: 'Comissões' },
    { id: 'balanco', label: 'Balanço (resumo)' },
    ...(temCadastroPublico ? [{ id: 'cadastro_publico', label: 'Cadastro Público' }] : []),
  ]

  const [selecionadas, setSelecionadas] = useState(SECOES_DISPONIVEIS.map(s => s.id))
  const [gerando, setGerando] = useState(false)
  const [erro, setErro] = useState('')

  const todasSelecionadas = selecionadas.length === SECOES_DISPONIVEIS.length

  const toggleSecao = (id) => {
    setSelecionadas(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id])
  }

  const toggleTodas = () => {
    setSelecionadas(todasSelecionadas ? [] : SECOES_DISPONIVEIS.map(s => s.id))
  }

  const handleImprimir = async () => {
    if (selecionadas.length === 0) {
      setErro('Selecione ao menos uma seção')
      return
    }
    try {
      setGerando(true)
      setErro('')
      // Mantém a ordem definida em SECOES_DISPONIVEIS, não a ordem de clique
      const secoesOrdenadas = SECOES_DISPONIVEIS.map(s => s.id).filter(id => selecionadas.includes(id))
      const blob = await clientesService.getFichaPdf(organizadorId, cliente.id, secoesOrdenadas)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      window.open(url, '_blank')
      onClose()
    } catch (err) {
      console.error('Erro ao gerar PDF:', err)
      setErro('Erro ao gerar PDF. Tente novamente.')
    } finally {
      setGerando(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 bg-blue-600">
          <div className="flex items-center gap-2 min-w-0">
            <Printer className="w-5 h-5 text-white flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-white font-semibold text-base">Imprimir Cliente</h2>
              <p className="text-white/80 text-xs truncate">{cliente?.nome}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <button
            type="button"
            onClick={toggleTodas}
            className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900"
          >
            {todasSelecionadas ? <CheckSquare size={16} /> : <Square size={16} />}
            {todasSelecionadas ? 'Desmarcar todas' : 'Selecionar todas (ficha completa)'}
          </button>

          <div className="space-y-1.5 border-t border-gray-100 pt-3">
            {SECOES_DISPONIVEIS.map((secao) => {
              const marcada = selecionadas.includes(secao.id)
              return (
                <label
                  key={secao.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={marcada}
                    onChange={() => toggleSecao(secao.id)}
                  />
                  <span className="text-sm text-gray-800">{secao.label}</span>
                </label>
              )
            })}
          </div>

          {erro && <p className="text-sm text-red-600">{erro}</p>}
        </div>

        <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-100 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleImprimir}
            disabled={gerando}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
          >
            {gerando ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
            {gerando ? 'Gerando...' : 'Gerar PDF'}
          </button>
        </div>
      </div>
    </div>
  )
}
